import {
  DocumentData,
  FirestoreError,
  getDocs,
  Query,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { getQuery, GetQueryProps } from 'services/api/firestore';
import { FetchState } from 'types';
import { devlog } from 'utilities';

type State = FetchState;

type Return<T> = {
  data: T[];
  docs: Array<QueryDocumentSnapshot<T>>;
  state: State;
  error: FirestoreError | null;
};

export const useDocuments = <Schema = DocumentData>(
  memoizedPropsOrQuery: GetQueryProps<Schema> | Query<Schema> | null,
): Return<Schema> => {
  const [state, setState] = useState<State>('loading');
  const [docs, setDocs] = useState<Array<QueryDocumentSnapshot<Schema>>>([]);
  const [error, setError] = useState<FirestoreError | null>(null);

  const query = useMemo(() => {
    if (!memoizedPropsOrQuery) return null;
    else if (memoizedPropsOrQuery instanceof Query) return memoizedPropsOrQuery;
    else return getQuery<Schema>(memoizedPropsOrQuery);
  }, [memoizedPropsOrQuery]);
  const data = useMemo(() => docs?.map((doc) => doc.data()) || [], [docs]);

  useEffect(() => {
    if (!query) return;

    getDocs<Schema, DocumentData>(query)
      .then((querySnapshot) => {
        setDocs(querySnapshot.docs);
        setState('success');
      })
      .catch((error) => {
        devlog('error', error);
        setError(error);
        setState('error');
      });
  }, [query]);

  return {
    data,
    docs,
    state,
    error,
  };
};
